import { ListFilter, RangeFilter } from '@sbt-web/network/types';
import { useViewport } from '@sbt-web/hooks';
import {
  FormGroup,
  FormLabel,
  SelectBox,
  SelectBoxSkeleton,
  SelectOption,
  SelectOptionOptionType,
} from '@sbt-web/ui';
import React from 'react';
import {
  mapValuesToOptions,
  useValueList,
  ValueListStatus,
} from '@client/hooks/useValueList';

interface Props {
  filter: RangeFilter;
  initialValues: { min?: string; max?: string };
  onSelect: (key: string, value?: string, label?: string) => void;
}

export const RangeSelector: React.FunctionComponent<Props> = (props) => {
  const { filter, initialValues, onSelect } = props;

  const { isMobile } = useViewport();

  const handleSelection = (filter: ListFilter, option: SelectOption[]) => {
    onSelect(
      filter.queryString,
      option.length ? option[0].value : undefined,
      option.length ? option[0].name : undefined
    );
  };

  const flavor = isMobile ? 'large' : 'small';

  const minValueListData = useValueList(filter.min.dataSource);
  const maxValueListData = useValueList(filter.max.dataSource);

  if (
    minValueListData.state === ValueListStatus.ERROR ||
    maxValueListData.state === ValueListStatus.ERROR
  )
    return;

  const minValueList = mapValuesToOptions(minValueListData.value);
  const maxValueList = mapValuesToOptions(maxValueListData.value);

  const currentMinIndex = initialValues.min
    ? minValueList.findIndex((v) => v.value === initialValues.min)
    : -1;
  const currentMinOption =
    currentMinIndex >= 0 ? minValueList[currentMinIndex] : undefined;

  const currentMaxIndex = initialValues.max
    ? maxValueList.findIndex((v) => v.value === initialValues.max)
    : -1;
  const currentMaxOption =
    currentMaxIndex >= 0 ? maxValueList[currentMaxIndex] : undefined;

  const isMaxSet = currentMaxOption !== undefined;
  const isMinSet = currentMinOption !== undefined;

  const minValueListOptions: SelectOptionOptionType[] = minValueList.map(
    (value) => ({
      ...value,
      disabled:
        // If the maximum value is not set, we don't want to disable any options
        isMaxSet && (value.weight ?? 0) > (currentMaxOption.weight ?? 0),
    })
  );

  const maxValueListOptions: SelectOptionOptionType[] = maxValueList.map(
    (value) => ({
      ...value,
      disabled:
        // If the minimum value is not set, we don't want to disable any options
        isMinSet && (value.weight ?? 0) < (currentMinOption.weight ?? 0),
    })
  );

  return (
    <FormGroup>
      <FormLabel>{filter.label}</FormLabel>

      {minValueListData.state === ValueListStatus.LOADED &&
      maxValueListData.state === ValueListStatus.LOADED ? (
        <div>
          <SelectBox
            nullable
            name={filter.min.uri}
            placeholder="Qualsiasi"
            inlineLabel={filter.min.label}
            selectedIndices={currentMinIndex >= 0 ? [currentMinIndex] : []}
            flavor={flavor}
            valueList={minValueListOptions}
            onSelect={(opt) => handleSelection(filter.min, opt)}
          />
          <SelectBox
            nullable
            name={filter.max.uri}
            placeholder="Qualsiasi"
            inlineLabel={filter.max.label === 'a' ? '   a' : filter.max.label}
            selectedIndices={currentMaxIndex >= 0 ? [currentMaxIndex] : []}
            flavor={flavor}
            valueList={maxValueListOptions}
            onSelect={(opt) => handleSelection(filter.max, opt)}
          />
        </div>
      ) : (
        <div>
          <SelectBoxSkeleton flavor={flavor} />
          <SelectBoxSkeleton flavor={flavor} />
        </div>
      )}
    </FormGroup>
  );
};
